/**
 * Dashboard API client (E3-1).
 *
 * Typed wrappers over the three read-only Command Center endpoints, layered on
 * the shared `apiRequest` from `client.ts` so they inherit the LWP session +
 * double-submit-CSRF behaviour (cookie credentials, `X-CSRFToken` echo) and the
 * `ApiError` status semantics. These are GETs, so no CSRF header is added, but
 * routing them through the same wrapper keeps base-URL, error, and JSON parsing
 * handling in one place.
 *
 * When `VITE_USE_MOCKS` is on (see `src/mocks/`), MSW intercepts these requests
 * in the browser/test runtime and answers them from the in-memory fixtures, so
 * the UI renders fully with no backend.
 */
import { apiRequest } from "./client";
import type {
  DashboardSummary,
  EngagementSummary,
  MemberDetail,
  MembersQuery,
  MembersResponse,
  SyncHealth,
} from "./dashboard.types";

/** Build a `?a=b&c=d` query string from defined params (skips undefined). */
function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** `GET /api/dashboard/summary` — the headline roll-up the Dashboard renders. */
export function getDashboardSummary(): Promise<DashboardSummary> {
  return apiRequest<DashboardSummary>("/dashboard/summary");
}

/**
 * `GET /api/members` — paginated, filterable member list (drill-down target;
 * read-only). All filters are optional; omitted ones are not sent.
 */
export function getMembers(query: MembersQuery = {}): Promise<MembersResponse> {
  const path = `/members${buildQuery({
    tier: query.tier,
    district: query.district,
    status: query.status,
    q: query.q,
    expiringWithin: query.expiringWithin,
    riskBand: query.riskBand,
    // Insights drill params (#282) — same dimensions/serialisation as getFacets.
    category: query.category,
    segment: query.segment,
    missing: query.missing,
    // Tri-state boolean → "true"/"false" (matches the backend `_bool_param`).
    hasEmail: query.hasEmail === undefined ? undefined : String(query.hasEmail),
    county: query.county,
    engagementBand: query.engagementBand,
    // Pulse worklist drills (#606) — real API filters now, not dropped params.
    renewal: query.renewal,
    lapsedWithin: query.lapsedWithin,
    // Legislative districts (#341). NB this list is an explicit allow-list: a
    // param missing from it is dropped silently and the request returns the
    // WHOLE roster, which is the same defect shape #592 / #606 / #652 kept
    // finding one layer down. Anything added to `MembersQuery` must be added
    // here too.
    usHouse: query.usHouse,
    stateSenate: query.stateSenate,
    stateHouse: query.stateHouse,
    // Which address they read (home, office, either; absent = the CE21 address on file).
    districtAddress: query.districtAddress,
    // An event's people (#705).
    event: query.event,
    eventGroup: query.eventGroup,
    // People → Licences (#635).
    licenseState: query.licenseState,
    // Licence reminders (#339).
    licenceReminder: query.licenceReminder,
    // Who an email reaches (#716).
    reachable: query.reachable,
    limit: query.limit,
    offset: query.offset,
  })}`;
  return apiRequest<MembersResponse>(path);
}

/**
 * `GET /api/members/:id` — the full read-only member detail behind the profile
 * drill-down (E3-4). Throws an `ApiError` with `status === 404` when no member
 * with that id exists, so the profile page can render a not-found state.
 */
export function getMember(id: number | string): Promise<MemberDetail> {
  return apiRequest<MemberDetail>(`/members/${id}`);
}

/** `GET /api/sync/health` — per-integration sync run health for the ops panel. */
export function getSyncHealth(): Promise<SyncHealth> {
  return apiRequest<SyncHealth>("/sync/health");
}

/**
 * `GET /api/dashboard/engagement` — the member engagement / lapse-risk
 * distribution the Command Center engagement card renders (E14-1).
 */
export function getEngagementSummary(): Promise<EngagementSummary> {
  return apiRequest<EngagementSummary>("/dashboard/engagement");
}
